//=====================================================================================
//
//	FoldableSectionsGfxApp
//
//=====================================================================================
//-------------------------------------------------------------------------------------
//	Imports
//-------------------------------------------------------------------------------------
import { GfxApp3D } from "../../GfxContainer/src/GfxApp3D.js";
import { GfxDrawing3D, GfxPath3D } from "../../GfxContainer/src/GfxDrawing3D.js";
import { GfxPath } from "../../GfxContainer/src/GfxDrawing.js";
import { Shapes3D, Math3D, MathUtil, PolygonList } from "../../VectorUtilsJS/src/VectorUtilLib.js";

//-------------------------------------------------------------------------------------
//	Constants
//-------------------------------------------------------------------------------------
// Floating point error allowance
let epsilon = 0.0001;

//-------------------------------------------------------------------------------------
//	Constructor
//-------------------------------------------------------------------------------------
function FoldableSectionsGfxApp(appName, appId, appDefaultSettings, appControlGroups)
{
	// Merge default settings from subclass
	let defaultSettings = Object.assign({}, FoldableSectionsGfxApp.defaultSettings);
	Object.assign(defaultSettings, appDefaultSettings);
	
	// appConfig is the same for all 'foldable' classes
	// (maybe allow a different filename later)
	let appConfig = {/*origin: "topleft", */ ypositive:"down", filename:"foldableDesign" };
	
	let appInfo = {
		appId: appId,
		name: appName, 
		defaultSettings: defaultSettings, 
		controlGroups: appControlGroups,
		appConfig: appConfig
	};
	
	GfxApp3D.call(this, appInfo);
}

FoldableSectionsGfxApp.prototype = Object.create(GfxApp3D.prototype);

Object.defineProperty(FoldableSectionsGfxApp.prototype, 'constructor', {
	value: FoldableSectionsGfxApp,
	enumerable: false,
	write: true });

//-------------------------------------------------------------------------------------
//	Default settings
//-------------------------------------------------------------------------------------
FoldableSectionsGfxApp.defaultSettings = 
	{
		edgeTabStyle:		"tabAndSlot",
		edgeTabInset:		1.0,
		edgeTabDepth:		8.0,
		edgeSlotInset:		4.0,
		edgeSlotDepth:		5.0,
		edgeSlotWidth:		0.0,
		edgeSlotOffset:		0.0,
		edgeSlotMargin:		1.0,
		edgeTabRadius:		2.0,
		edgeTabOffset:		1.0,
		edgeTabUndercut:	0.0,
		edgeTabInnerRadius:	0.5,
		seamTabStyle:		"tabAndSlot",
		seamTabCount:		1,
		clipToSafeArea:		false,
	}

//-------------------------------------------------------------------------------------
//	Controls and Control Groups
//-------------------------------------------------------------------------------------
	
FoldableSectionsGfxApp.tabSelection = 
	[
		{ label: "None",				value: "cut"			},
		{ label: "Symmetrical Tabs",	value: "symmetrical"	},
		{ label: "Tabs and Slots",		value: "tabAndSlot"		},
		{ label: "Tabs only",			value: "tabOnly"		}
	];

FoldableSectionsGfxApp.tabAndSlotControls =
	[
		{ label: "Edge tab", 				type: "selection",	prop: "edgeTabStyle",			options: FoldableSectionsGfxApp.tabSelection	},
		{ label: "Inset",					type: "number",		prop: "edgeTabInset",			min:0, max: 10, step:0.1, entry:"yes"		},
		{ label: "Tab depth",				type: "number",		prop: "edgeTabDepth",			min:0, max: 20, step:0.1, entry:"yes"		},
		{ label: "Slot inset",				type: "number",		prop: "edgeSlotInset",			min:0, max: 10, step:0.1, entry:"yes"		},
		{ label: "Slot depth",				type: "number",		prop: "edgeSlotDepth",			min:0, max: 20, step:0.1, entry:"yes"		},
		{ label: "Slot width",				type: "number",		prop: "edgeSlotWidth",			min:0, max: 5, step:0.1, entry:"yes"		},
		{ label: "Slot offset",				type: "number",		prop: "edgeSlotOffset",			min:0, max: 5, step:0.1, entry:"yes"		},
		{ label: "Slot margin",				type: "number",		prop: "edgeSlotMargin",			min:-2, max: 5, step:0.1, entry:"yes"		},
		{ label: "Radius",					type: "number",		prop: "edgeTabRadius",			min:0, max: 20, step:0.1, entry:"yes"		},
		{ label: "Edge Radius",				type: "number",		prop: "edgeTabInnerRadius",		min:0, max: 5, step:0.1, entry:"yes"		},
		{ label: "Tab Offset",				type: "number",		prop: "edgeTabOffset",			min:0, max: 5, step:0.1, entry:"yes"		},
		{ label: "Tab Undercut",			type: "number",		prop: "edgeTabUndercut",		min:0, max: 5, step:0.1, entry:"yes"		},
		{ label: "Seam tab", 				type: "selection",	prop: "seamTabStyle",			options: FoldableSectionsGfxApp.tabSelection	},
		{ label: "Seam tab count",			type: "number",		prop: "seamTabCount",			min:1, max: 10, step:1, entry:"yes"			},
		{ label: "Clip to safe area",		type: "boolean",	prop: "clipToSafeArea"						}
	];



//-------------------------------------------------------------------------------------
//	Get Status
//-------------------------------------------------------------------------------------
FoldableSectionsGfxApp.prototype.GetStatus = function()
{
	var status = undefined;
	
	if (this.generateMessageStr != undefined)
		status = {msg:this.generateMessageStr };
		
	return status;
}

//-------------------------------------------------------------------------------------
//	Get Edge Config
//-------------------------------------------------------------------------------------
FoldableSectionsGfxApp.prototype.GetEdgeConfig = function()
{
	let edgeConfig = 
	{
		edgeTabStyle:       this.settings.edgeTabStyle,
		edgeTabInset:       this.settings.edgeTabInset,
		edgeTabDepth:       this.settings.edgeTabDepth,
		edgeTabRadius:      this.settings.edgeTabRadius,
		edgeSlotInset:      this.settings.edgeSlotInset,
		edgeSlotDepth:      this.settings.edgeSlotDepth,
		edgeSlotWidth:      this.settings.edgeSlotWidth,
		edgeSlotMargin:     this.settings.edgeSlotMargin,
		edgeSlotOffset:      this.settings.edgeSlotOffset,
		edgeTabInnerRadius: this.settings.edgeTabInnerRadius,
		edgeTabOffset:      this.settings.edgeTabOffset,
		edgeTabUndercut:    this.settings.edgeTabUndercut,
		seamTabStyle:       this.settings.seamTabStyle,
		seamTabCount:       this.settings.seamTabCount,
		clipToSafeArea:     this.settings.clipToSafeArea
	};
		 
	 return edgeConfig;
}


//-------------------------------------------------------------------------------------
//	Point Vector Add
//-------------------------------------------------------------------------------------
var PointVectorAdd = function(p, v, s) 
{
	var pt = {};
	pt.x = p.x + v.x * s;
	pt.y = p.y + v.y * s;
	
	return pt;
}

//-------------------------------------------------------------------------------------
//	Calculate Safe Area
//
//		rampLen:      Length of the diagonal "not safe" area.
//		rampDistance: Length along the edge of the "not safe". Max value should be len/2
//		rampHeight:   Height of the not "safe area". May be less than depth 
//		rampVecA:     vector from ptA defining edge of safe area
//		rampVecB:     vector from ptB
//-------------------------------------------------------------------------------------
FoldableSectionsGfxApp.prototype.CalcSafeArea = function(ptA, ptB, depth)
{
	var ramp = {rampVecA:undefined, rampVecB:undefined, rampLen:undefined, rampDistance:undefined, rampHeight:undefined};
	
	// For curved corners and to insure tabs will fit when folded into sharp corners
	var sa = MathUtil.CalcAngle(ptA, ptB);
	
	let len = MathUtil.DistanceBetween(ptA, ptB);
	
	// "aa" is the direction of the horizontal line for the edge, either 0 or 180 degrees
	let aa = (ptA.y < ptB.y) ? 0 : Math.PI;
	
	// 180 degrees from the angle of the edge
	let sb = Math.PI + sa; 

	// The "safe angle" for both edge.ptA and edge.ptB, relative to 0 degrees		
	var s3, s4;
	if (sa > 0)
	{
		s3 = (sa > Math.PI/2) ? (2 * sa - Math.PI) : aa;
		s4 = (sa > Math.PI/2) ? aa : (sb + sa);
	}
	else
	{
		s3 = (sa > -Math.PI/2) ? (2 * sa) : aa;
		s4 = (sa > -Math.PI/2) ? aa : (2 * sb);
	}
	
	// Calculate the length of the ramp for the safe area. We are limited by the
	// length of the edge (from ptA to ptB) and the depth of the slot.
	let tc = undefined;
	let ts = undefined;
	if (sa != Math.PI/2)
		tc = len/(2*Math.abs(Math.cos(sa)));
	
	if (sa != 0)
		ts = depth/(Math.abs(Math.sin(sa))); 
		
	if (ts != undefined && tc != undefined)
	{
		ramp.rampLen = (ts < tc) ? ts : tc;
	}
	else if (ts != undefined)
		ramp.rampLen = ts;
	else if (tc != undefined)
		ramp.rampLen = tc;
		
	ramp.rampHeight   = ramp.rampLen * Math.abs(Math.sin(sa));
	ramp.rampDistance = ramp.rampLen * Math.abs(Math.cos(sa));
	ramp.rampDistanceB = len - ramp.rampDistance;
		
	//let us = {x:Math.cos(aa), y:Math.sin(aa)}; // Horizontal vector pointing outside of section
	//let ua = {x:Math.cos(sa), y:Math.sin(sa)}; // Vector pointing from ptA to ptB
	//let ub = {x:Math.cos(sb), y:Math.sin(sb)}; // Vector pointing from ptB to ptA
	ramp.rampVecA = {x:Math.cos(s3), y:Math.sin(s3)}; // Vector pointing from ptA that shows clipping of slot or tab
	ramp.rampVecB = {x:Math.cos(s4), y:Math.sin(s4)}; // Vector pointing from ptB that shows clipping of slot or tab
	
	return ramp;
}

//-------------------------------------------------------------------------------------
//	Render Simple Tab
//
//-------------------------------------------------------------------------------------
FoldableSectionsGfxApp.prototype.RenderSimpleTab = function(paths, ePtA, ePtB, wSlot, wTab, tabInset, depth, r, ramp)
{
	var pt;
	
	let uv = MathUtil.CalcUnitVector(ePtA, ePtB);
	let up = {x:uv.y, y:-uv.x};
	
	// Constants
	let q = 3.14159/2; // Quarter turn, for arc corners

	// Angle of the edge. Used with arc for curved corners
	var sa = MathUtil.CalcAngle(ePtA, ePtB);
	
	let len = MathUtil.DistanceBetween(ePtA, ePtB);

	// Clamp the corner radius. It should not be negative or larger than the depth or half the width
	if (r < 0)
		r = 0;
		
	if (r > depth)
		r = depth;

	if (r > wSlot/2)
		r = wSlot/2;			

	// Determine if we have to clip the slot to the "safe area"
	let clipSlot = false;
	if ((ramp!=undefined) && (tabInset < ramp.rampDistance))
	{
		// If the center of the rounded corner is "outside" the ramp, then
		// we clip to the ramp. Note that we are not looking at a point, but just
		// one dimension of the center of the circle
		if (tabInset + r < ramp.rampDistance)
		{
			clipSlot = true;
		}
		// Else, determine if the rounded corner intersects the diagonal "ramp" of the safe area.
		else
		{
			// Center of the quarter-circle at the corner
			let ctr = PointVectorAdd(ePtA, uv, tabInset + r);
			ctr =  PointVectorAdd(ctr, up, depth - r);
		
			// Distance from the center of the quarter-circle to the ramp line
			let linePtB = PointVectorAdd(ePtA, ramp.rampVecA, ramp.rampLen);
			let ptLineDist = MathUtil.CalcPointToLineDistance(ctr, ePtA, linePtB);
		
			clipSlot = (ptLineDist < r);
		}
	}

	// If we are clipping the slot, then we don't use the radius value and
	// instead calculate points on the "ramp"
	if (clipSlot)
	{
		var h;
		let w = tabInset;
		
		if (w > 0)
		{
			pt = PointVectorAdd(ePtA, uv, w);
			paths.cutPath.LineTo(pt);

			h = w * Math.abs(Math.tan(sa));
			pt = PointVectorAdd(pt, up, h);
			paths.cutPath.LineTo(pt);
		}
		
		w = len - tabInset;
		
		if (w >= ramp.rampDistance - epsilon)
		{
			pt = PointVectorAdd(ePtA, uv, ramp.rampDistance);
			pt = PointVectorAdd(pt, up, ramp.rampHeight);
			paths.cutPath.LineTo(pt);
		}

		if (w > ramp.rampDistanceB && Math.abs(ramp.rampDistanceB - ramp.rampDistance) > epsilon)
		{
			pt = PointVectorAdd(ePtA, uv, ramp.rampDistanceB);
			pt = PointVectorAdd(pt, up, ramp.rampHeight);
			paths.cutPath.LineTo(pt);
		}
		
		if (w < ramp.rampDistance)
			h = w * Math.abs(Math.tan(sa));
		else if (w < ramp.rampDistanceB)
			h = depth;
		else
			h = (len - w) * Math.abs(Math.tan(sa));
		
		if (h > epsilon)
		{
			pt = PointVectorAdd(ePtA, uv, w);
			pt = PointVectorAdd(pt, up, h);
			paths.cutPath.LineTo(pt);
		}
		
		pt = PointVectorAdd(ePtA, uv, w);
		paths.cutPath.LineTo(pt);
	}
	else
	{
		pt = PointVectorAdd(ePtA, uv, tabInset);
		if (tabInset > 0)
			paths.cutPath.LineTo(pt);

		pt = PointVectorAdd(pt, up, depth - r);
		paths.cutPath.LineTo(pt);

		if (r > 0)
		{
			pt = PointVectorAdd(pt, uv, r);
			paths.cutPath.Arc(pt, r, sa + 2 * q , sa + 3 * q, 0);
			pt = PointVectorAdd(pt, up, r);
		}

		pt = PointVectorAdd(pt, uv, wSlot - 2 * r);
		paths.cutPath.LineTo(pt);

		if (r > 0)
		{
			pt = PointVectorAdd(pt, up, -r);
			paths.cutPath.Arc(pt, r, sa + 3 * q , sa, 0);
			pt = PointVectorAdd(pt, uv, r);
		}

		pt = PointVectorAdd(pt, up, -(depth - r));
		paths.cutPath.LineTo(pt);
	}
}

//-------------------------------------------------------------------------------------
//	Add Cut Edge
//-------------------------------------------------------------------------------------
FoldableSectionsGfxApp.prototype.AddCutEdge = function(paths, edge, cutConfig)
{
	// Edge info
	var uv = MathUtil.CalcUnitVector(edge.ptA, edge.ptB);
	var up = {x:uv.y, y:-uv.x};
	var len = MathUtil.DistanceBetween(edge.ptA, edge.ptB);
	
	// Config
	let minTabWidth = 2; // mm
	
	// Constants
	let q = 3.14159/2; // Quarter turn, for arc corners
	
	// Parameters
	var tabInset = cutConfig.edgeTabInset;
	var tabDepth = cutConfig.edgeTabDepth;
	var slotDepth = cutConfig.edgeSlotDepth;
	var slotInset = cutConfig.edgeSlotInset;
	var slotWidth = cutConfig.edgeSlotWidth;
	var slotOffset = cutConfig.edgeSlotOffset;
	var seamTabCount = cutConfig.seamTabCount;
	var slotMargin  = cutConfig.edgeSlotMargin;
	var tabOffset = cutConfig.edgeTabOffset;
	var tabUndercut = cutConfig.edgeTabUndercut;
	let r = cutConfig.edgeTabRadius;
	let er = cutConfig.edgeTabInnerRadius;
	let ramp = undefined; 
	var tabCount = 1; // May be greater than one for seams
	let clipToSafeArea = cutConfig.clipToSafeArea;
	let showSafeArea = (paths.debugPath != undefined);
	
	var edgeStyle;
	if (edge.type == "seamBottom" || edge.type == "seamTop")
	{
		edgeStyle = cutConfig.seamTabStyle;
		tabCount = seamTabCount;
	}
	else if (edge.type == "rightCut" || edge.type == "leftCut")
	{
		edgeStyle = cutConfig.edgeTabStyle;
	}
	else
	{
		edgeStyle = "cut";
	}
	
	// If we are not adding slots to go with the tabs, then don't use the slotInset
	if (edgeStyle == "tabOnly")
	{
		slotInset = 0;
		slotMargin = 0;
	}

	var pt = {x:edge.ptA.x, y:edge.ptA.y};

	// Angle of the edge. Used with arc for curved corners
	var sa = MathUtil.CalcAngle(edge.ptA, edge.ptB);

	// Determine if we need to calculate the safe area values
	let calcSafeAreaFlag = 
		((edge.type == "rightCut" && edgeStyle != "tabOnly") || edge.type == "leftCut") && 
		(edgeStyle != "cut") && 
		(clipToSafeArea || showSafeArea);
	
	// Calculate the safe area. Note that the "safe angle" for an edge is the acute angle
	// between the edge and the horizontal.
	if (calcSafeAreaFlag)
	{
		// Depth of the tab or slot
		var d = (edge.type == "rightCut" && edgeStyle == "tabAndSlot") ? slotDepth : tabDepth;

		// Calculate the values
		ramp = this.CalcSafeArea(edge.ptA, edge.ptB, d);

		// Render the safe area
		if (showSafeArea)
		{
			var p2;

			paths.debugPath.MoveTo(edge.ptA);
			
			p2 = PointVectorAdd(edge.ptA, ramp.rampVecA, ramp.rampLen);
			paths.debugPath.LineTo(p2);

			p2 = PointVectorAdd(edge.ptB, ramp.rampVecB, ramp.rampLen);
			paths.debugPath.LineTo(p2);
			
			paths.debugPath.LineTo(edge.ptB);
		}
	}

	if (edgeStyle == "cut")
	{
		// Nothing needed here; will default to a straight cut
	}
	else if (edgeStyle == "symmetrical")
	{
		// We can have multiple tabs for seams, so adjust the length accordingly
		len = len/tabCount;
		
		// Calc the width of the tab. Note that we use half the length, since we
		// are rendering symmetrical tabs and we want to cover only half of the edge.
		let w = len/2 - tabInset * 2;
		
		if (w >= minTabWidth)
		{
			for (var k = 0; k < tabCount; k++)
			{
				// Calc the start and end of this tab.
				// Note that since these are symmetrical, we use len/2 for ePtB
				let ePtA = PointVectorAdd(edge.ptA, uv, len * k);
				let ePtB = PointVectorAdd(ePtA, uv, len/2);
				
				this.RenderSimpleTab(paths, ePtA, ePtB, w, 0, tabInset, tabDepth, r, (clipToSafeArea ? ramp : undefined));

				// Score line
				pt = PointVectorAdd(ePtA, uv, tabInset);
				paths.scorePath.MoveTo(pt);
				pt = PointVectorAdd(ePtB, uv, -tabInset);
				paths.scorePath.LineTo(pt);

				// If rendering multiple tabs, then move to the end of this tab
				if (k < tabCount - 1)
				{
					pt = PointVectorAdd(ePtA, uv, len);
					paths.cutPath.LineTo(pt);
				}
			}
		}
	}
	else if (edgeStyle == "tabAndSlot" || edgeStyle == "tabOnly")
	{
		// We can have multiple tabs for seams, so adjust the length accordingly
		len = len/tabCount;
		
		// Calc the width of the slot and the tab
		let wSlot = len - tabInset * 2;
		let wTab = wSlot - slotInset * 2;
		
		// Iterate for multiple tabs and slots, but there is usually just one pass
		for (var k = 0; k < tabCount; k++)
		{
			// Calc the start and end of this edge
			let ePtA = PointVectorAdd(edge.ptA, uv, len * k);
			let ePtB = PointVectorAdd(ePtA, uv, len);
			
			pt = {x:ePtA.x, y:ePtA.y};
			
			// Edges marked "rightCut" or "seamTop" are rendered as slots
			// But don't render slots if we doing "tabOnly" or if the slot is not wide enough
			if ((edge.type == "rightCut" || edge.type == "seamTop") && (edgeStyle != "tabOnly") && (wSlot > minTabWidth))
			{
				this.RenderSimpleTab(paths, ePtA, ePtB, wSlot, wTab, tabInset, slotDepth, r, (clipToSafeArea ? ramp : undefined))

				// If the tab that goes into this slot is wide enough, then add the cut lines
				// for the slot and the score lines on both sides
				if (wTab > minTabWidth)
				{
					pt = PointVectorAdd(ePtA, uv, tabInset);
					paths.scorePath.MoveTo(pt);
					pt = PointVectorAdd(pt, uv, slotInset - slotMargin);
					paths.scorePath.LineTo(pt);

					pt = PointVectorAdd(ePtB, uv, -tabInset);
					paths.scorePath.MoveTo(pt);
					pt = PointVectorAdd(pt, uv, -(slotInset - slotMargin));
					paths.scorePath.LineTo(pt);
					
					// Calc location to cut slot
					var ptB = PointVectorAdd(ePtA, uv, tabInset + slotInset - slotMargin);
					ptB = PointVectorAdd(ptB, up, slotOffset);
					paths.slotCutPath.MoveTo(ptB);
					ptB = PointVectorAdd(ptB, uv, wTab + 2 * slotMargin);
					paths.slotCutPath.LineTo(ptB);
		
					// If slot width is non-zero, then cut a rectangle
					if (slotWidth > 0)
					{
						ptB = PointVectorAdd(ptB, up, slotWidth);
						paths.slotCutPath.LineTo(ptB);
						ptB = PointVectorAdd(ptB, uv, -(wTab + 2 * slotMargin));
						paths.slotCutPath.LineTo(ptB);
						paths.slotCutPath.Close();
					}
				}
				// Otherwise, just render the score line across entire slot
				else
				{
					pt = PointVectorAdd(ePtA, uv, tabInset);
					paths.scorePath.MoveTo(pt);
					pt = PointVectorAdd(ePtB, uv, -tabInset);
					paths.scorePath.LineTo(pt);
				}
			}
			
			// Edges marked "leftCut" or "seamBottom are rendered as tabs
			if ((edge.type == "leftCut" || edge.type == "seamBottom") && (wTab > minTabWidth))
			{
				if (r > tabDepth)
					r = tabDepth;
				
				if (r > wTab/2)
					r = wTab/2;
					
				if (er > wTab/2)
					er = wTab/2;
					
				if (er > tabDepth - r)
					er = tabDepth - r;
					
				if (tabOffset > tabDepth - r - er)
					tabOffset = tabDepth - r - er;
					
				if (er < epsilon)
					tabOffset = 0;
					
				// Spacing between the two arcs that make up the corners
				let cspacing = tabDepth - tabOffset - r - er;
					

				// Determine if we have to clip the slot to the "safe area"
				let clipTab = 0;
				let edgeIntersect = undefined;
				if (clipToSafeArea && (ramp!=undefined) && (tabInset + slotInset < ramp.rampDistance))
				{
					// Calc where the tab edge intersects the ramp
					edgeIntersect = (tabInset + slotInset) * Math.tan(sa);
					// Distance from outer edge of tab to the ramp
					let j = tabDepth - edgeIntersect;
					
					// Does it clip only the corner?
					if (j < r)
						clipTab = 1;
					// Does it clip the straight edge (between the curved corners)?
					else if (cspacing > 0 && j < r + cspacing)
						clipTab = 2;
					// It must clip the inner corner
					else /*if (j < r + cspacing + er)*/
						clipTab = 3;
					
					// If the center of the rounded corner is "outside" the ramp, then
					// we clip to the ramp. Note that we are not looking at a point, but just
					// one dimension of the center of the circle
					//if (tabInset + r < ramp.rampDistance)
					//{
					//	clipSlot = true;
					//}
					// Else, determine if the rounded corner intersects the diagonal "ramp" of the safe area.
					//else
					if (clipTab == 1)
					{
						// Center of the quarter-circle at the corner
						let ctr = PointVectorAdd(ePtA, uv, tabInset + slotInset + r);
						ctr =  PointVectorAdd(ctr, up, tabDepth - r);
	
						// Distance from the center of the quarter-circle to the ramp line
						let linePtB = PointVectorAdd(ePtA, ramp.rampVecA, ramp.rampLen);
						let ptLineDist = MathUtil.CalcPointToLineDistance(ctr, ePtA, linePtB);
	
						clipTab = (ptLineDist < r) ? 1 : 0;
					}
				}

				// Render the tab
				pt = PointVectorAdd(pt, uv, tabInset + slotInset);
				paths.cutPath.LineTo(pt);
				
				if (clipTab < 3) // clipTab == 2, 1, or 0
				{
					if (er > 0)
					{
						pt = PointVectorAdd(pt, uv, er);
						if (tabOffset > 0)
						{
							paths.cutPath.LineTo(pt);
							pt = PointVectorAdd(pt, up, tabOffset);
							paths.cutPath.LineTo(pt);
						}

						pt = PointVectorAdd(pt, up, er);
						paths.cutPath.Arc(pt, er, sa + 1 * q , sa + 2 * q, 0);
						pt = PointVectorAdd(pt, up, -(er + tabOffset));
						pt = PointVectorAdd(pt, uv, -er);
					}
				}
				else
				{
					if (er > 0)
					{
						pt = PointVectorAdd(pt, uv, er);
						if (tabOffset > 0)
						{
							paths.cutPath.LineTo(pt);
							pt = PointVectorAdd(pt, up, tabOffset);
							paths.cutPath.LineTo(pt);
						}

						pt = PointVectorAdd(pt, up, er);
					}
				}

				if (clipTab < 2) // clipTab == 1 or 0, clipping only the corner, but not the side
				{
					if (tabDepth - r > 0)
					{
						pt = PointVectorAdd(ePtA, uv, (tabInset + slotInset));
						pt = PointVectorAdd(pt, up, tabDepth - r);
						paths.cutPath.LineTo(pt);
					}
				}
			
				if (clipTab < 1) // clipTab == 0, no clipping
				{
					if (r > 0)
					{
						pt = PointVectorAdd(pt, uv, r);
						paths.cutPath.Arc(pt, r, sa + 2 * q , sa + 3 * q, 0);
						pt = PointVectorAdd(pt, up, r);
					}

					if (wTab - 2 * r > 0)
					{
						pt = PointVectorAdd(pt, uv, wTab - 2 * r);
						paths.cutPath.LineTo(pt);
					}
			
					if (r > 0)
					{
						pt = PointVectorAdd(pt, up, -r);
						paths.cutPath.Arc(pt, r, sa + 3 * q , sa, 0);
						pt = PointVectorAdd(pt, uv, r);
					}
				}
				else // clipping
				{
					pt = PointVectorAdd(ePtA, uv, tabInset + slotInset);
					pt = PointVectorAdd(pt, up, edgeIntersect);
					paths.cutPath.LineTo(pt);
					
					pt = PointVectorAdd(ePtA, uv, ramp.rampDistance);
					pt = PointVectorAdd(pt, up, ramp.rampHeight);
					paths.cutPath.LineTo(pt);

					pt = PointVectorAdd(ePtB, uv, -ramp.rampDistance);
					pt = PointVectorAdd(pt, up, ramp.rampHeight);
					paths.cutPath.LineTo(pt);

					pt = PointVectorAdd(ePtB, uv, -(tabInset + slotInset));
					pt = PointVectorAdd(pt, up, edgeIntersect);
					paths.cutPath.LineTo(pt);
				}

				if (clipTab < 2)
				{
					if (tabDepth - r > 0)
					{
						pt = PointVectorAdd(ePtB, uv, -(tabInset + slotInset));
						pt = PointVectorAdd(pt, up, tabDepth - r);
						paths.cutPath.LineTo(pt);
					}
				}
				
				if (clipTab < 3) // clipTab == 2, 1, or 0
				{
					if (er > 0)
					{
						// Short edge between outside corner and inside corner
						pt = PointVectorAdd(ePtB, uv, -(tabInset + slotInset));
						pt = PointVectorAdd(pt, up, er + tabOffset);
						//if (r + er + tabOffset < tabDepth)
						paths.cutPath.LineTo(pt);
						
						// Insert curved corner
						pt = PointVectorAdd(ePtB, uv, -(tabInset + slotInset + er));
						pt = PointVectorAdd(pt, up, er + tabOffset);
						paths.cutPath.Arc(pt, er, sa + 0 * q , sa + 1 * q, 0);
						pt = PointVectorAdd(pt, up, -er);
					
						if (tabOffset > 0)
						{
							pt = PointVectorAdd(pt, up, -tabOffset);
							paths.cutPath.LineTo(pt);
						}
					}
				}
				else
				{
					if (er > 0)
					{
						pt = PointVectorAdd(ePtB, uv, -(tabInset + slotInset + er));
						if (tabOffset > 0)
						{
							pt = PointVectorAdd(pt, up, tabOffset);
							paths.cutPath.LineTo(pt);
						}
					}
				}

				pt = PointVectorAdd(ePtB, uv, -(tabInset + slotInset + er));
				paths.cutPath.LineTo(pt);
				
				// Add score line
				let scoreInset = tabInset + slotInset + er
				pt = PointVectorAdd(ePtA, uv, scoreInset);
				paths.scorePath.MoveTo(pt);
				pt = PointVectorAdd(ePtB, uv, -scoreInset);
				paths.scorePath.LineTo(pt);

				// If rendering multiple tabs, then move to the end of this tab
				if (k < tabCount - 1)
					pt = PointVectorAdd(pt, uv, tabInset + slotInset);
			}
		}
	}
	else
	{
		// Nothing needed here; will default to a straight cut
	}

	// We always move to the last point on the edge
	paths.cutPath.LineTo(edge.ptB);

}


//-------------------------------------------------------------------------------------
//	Measure Sizes
//-------------------------------------------------------------------------------------
FoldableSectionsGfxApp.prototype.MeasureSizes = function(objects)
{
	var msg = undefined;

	var b = undefined;
	if (objects.cutPath != undefined)
		b = objects.cutPath.FindBounds();
	else if (objects.cutBounds != undefined)
		b = objects.cutBounds;

	if (b != undefined)
	{
		var cutSize = {x:Math.floor(b.max.x - b.min.x), y:Math.floor(b.max.y - b.min.y)};
		// msg
		msg = "Unwrapped: " + cutSize.x + " x " + cutSize.y + " mm, ";
		msg += Math.floor(cutSize.x/2.54)/10 + " x "  + Math.floor(cutSize.y/2.54)/10 + " in";
	}

	b = undefined;
	if (objects.solidPath != undefined)
		b = objects.solidPath.FindBounds();
	else if (objects.solidBounds != undefined)
		b = objects.solidBounds;
		
	if (b != undefined)
	{
		var solidSize = {x:Math.floor(b.max.x - b.min.x), y:Math.floor(b.max.y - b.min.y), z:Math.floor(b.max.z - b.min.z)};
		// msg
		if (msg != undefined)
			msg += "<br>";
		else
			msg = "";

		msg += "Solid: " + solidSize.x + " x " + solidSize.y + " x " + solidSize.z + " mm, ";
		msg += Math.floor(solidSize.x/2.54)/10 + " x "  + Math.floor(solidSize.y/2.54)/10 + " x " + Math.floor(solidSize.z/2.54)/10 + " in";
	}
	
	if (objects.sectionCount != undefined)
	{
		let s = (objects.sectionCount == 1) ? " piece" : " pieces";
		msg += "<br>";
		msg += objects.sectionCount + s + " needed";
	}
	
	this.generateMessageStr = msg;
}

//-------------------------------------------------------------------------------------
//
//	Foldable Sections Torus
//
//		Create plans for foldable sections for a torus
//
//-------------------------------------------------------------------------------------


//-------------------------------------------------------------------------------------
//	Constructor
//-------------------------------------------------------------------------------------
function FoldableSectionsTorusGfxApp()
{
	FoldableSectionsGfxApp.call(this, 
		"Foldable Torus Designer", 
		"FoldableSectionsTorusGfxAppId",
		FoldableSectionsTorusGfxApp.defaultSettings, 
		FoldableSectionsTorusGfxApp.controlGroups);
}

FoldableSectionsTorusGfxApp.prototype = Object.create(FoldableSectionsGfxApp.prototype);

Object.defineProperty(FoldableSectionsTorusGfxApp.prototype, 'constructor', {
	value: FoldableSectionsTorusGfxApp,
	enumerable: false,
	write: true });

//-------------------------------------------------------------------------------------
//	Default settings
//-------------------------------------------------------------------------------------
FoldableSectionsTorusGfxApp.defaultSettings = 
	{
		outerRadius:		100,
		innerRadius:		20,
		segments:			12,
		facesPerSegment:	8,
		faceRotation:		0,
		showCutAndScore:	false,
		showOneSection:		false,
		showTorus:			true,
		addOverlap:			false,
		sectionCount:		1,
		sectionConnect:		0,
		addSectionOutline:	false,
		sectionOutlineInset:0.0,
		addPanelOutline:	false,
		panelOutlineInset:  2.0
	}


//-------------------------------------------------------------------------------------
//	Controls and Control Groups
//-------------------------------------------------------------------------------------
FoldableSectionsTorusGfxApp.generalControls =
	[
		{ label: "Radius",			type: "number",		prop: "outerRadius",			min:1, max: 200, step:1, entry:"yes"	},
		{ label: "Radius2",			type: "number",		prop: "innerRadius",			min:1, max: 200, step:1, entry:"yes"	},
		{ label: "Segments",		type: "number",		prop: "segments",				min:3, max: 24, step:1, entry:"yes"	},
		{ label: "Faces",			type: "number",		prop: "facesPerSegment",		min:3, max: 24, step:1, entry:"yes"	},
		{ label: "Rotation",		type: "number",		prop: "faceRotation",			min:0, max: 360, step:1, entry:"yes"	},
	];

FoldableSectionsTorusGfxApp.displayControls =
	[
		{ label: "Show torus",						type: "boolean",	prop: "showTorus"				},
		{ label: "&nbsp;&nbsp;Show one section",	type: "boolean",	prop: "showOneSection"	 		},
		{ label: "Show cut and score",				type: "boolean",	prop: "showCutAndScore"			}
	];
	
FoldableSectionsTorusGfxApp.sectionControls =
	[
		{ label: "Add overlap face",		type: "boolean",	prop: "addOverlap"		 		},
		{ label: "Count",					type: "number",		prop: "sectionCount",			min:1, max: 24, step:1, entry:"yes"	},
		{ label: "Connected face",			type: "number",		prop: "sectionConnect",			min:0, max: 24, step:1, entry:"yes"	},
		{ label: "Include section outlines",type: "boolean",	prop: "addSectionOutline"		},
		{ label: "&nbsp;&nbsp;&nbsp;Inset",	type: "number",		prop: "sectionOutlineInset",	min:0, max: 10, step:0.1, entry:"yes"	},
		{ label: "Include panel outlines",	type: "boolean",	prop: "addPanelOutline"			},
		{ label: "&nbsp;&nbsp;&nbsp;Inset",	type: "number",		prop: "panelOutlineInset",		min:0, max: 10, step:0.1, entry:"yes"	}
	];

FoldableSectionsTorusGfxApp.controlGroups = 
	[
		{ label: "Torus",			controls: FoldableSectionsTorusGfxApp.generalControls		},
		{ label: "Display",			controls: FoldableSectionsTorusGfxApp.displayControls		},
		{ label: "Section",			controls: FoldableSectionsTorusGfxApp.sectionControls		},
		{ label: "Tabs",			controls: FoldableSectionsGfxApp.tabAndSlotControls	}
	];

//-------------------------------------------------------------------------------------
//	Generate
//-------------------------------------------------------------------------------------

function PolygonListToGfxPath(polyList, inset)
{
	var gfxPath = new GfxPath();
	
	var pl = (inset > epsilon) ? polyList.CalcOffsetPolygon(-inset) : polyList;
	
	for (var jj = 0; jj < pl.GetPolygonCount(); jj++)
	{
		var p = pl.GetPolygonPoints(jj);
		
		gfxPath.MoveTo(p[0]);
		for (var kk = 1; kk < p.length; kk++)
			gfxPath.LineTo(p[kk]);
		gfxPath.Close();
	}
	
	return gfxPath;
}

FoldableSectionsTorusGfxApp.prototype.Generate = function()
{
	this.gfxDrawing = new GfxDrawing3D();

	var outerRadius     = this.settings.outerRadius;
	var innerRadius     = this.settings.innerRadius;
	var segments        = this.settings.segments;
	var facesPerSegment = this.settings.facesPerSegment;
	var faceRotation    = this.settings.faceRotation * Math.PI / 180.0;
	var sectionCount    = this.settings.sectionCount;
	
	// Don't allow creation of self-intersecting torus. This prevents some
	// exceptions computing values
	if (outerRadius <= innerRadius)
		outerRadius = innerRadius + epsilon;
	
	// If generating multiple connected cut-and-score section, then limit this to the
	// number of segments.
	if (sectionCount > segments)
		sectionCount = segments;
	
	var linesS = [];

	// Keep the face count reasonable
	if (facesPerSegment < 3)
		facesPerSegment = 3;
		
	// Insure the segment count is reasonable 
	if (segments < 3)
		segments = 3;
	// Note that this is 1/2 the segment angle, since we are doing
	// plus and minus the angle below.
	let segmentAngle = Math.PI / segments;
	let segmentsToShow = segments;
	
	if (this.settings.showOneSection)
		segmentsToShow = 1;
	
	var fullGfxPath = new GfxPath3D();
	for (var j = 0; j < segmentsToShow; j++)
	{
		var smallCircleAngle = (j / segments) * Math.PI * 2.0;
		
		var ptC = {};
		ptC.x = outerRadius * Math.cos(smallCircleAngle);
		ptC.y = outerRadius * Math.sin(smallCircleAngle);
		ptC.z = 0;

		// Normal (tangent) of circle with radius R at ptC
		var normalC = {x:-ptC.y, y:ptC.x, z:0};
		normalC = Math3D.UnitVec3(normalC);

		var planeUnitU = Math3D.UnitVec3(ptC);
		var planeUnitV = Math3D.CrossProduct(normalC, planeUnitU);
		planeUnitV = Math3D.UnitVec3(planeUnitV);

		// Point D: a point on the circle with radius R
		var ptD = {};

		ptD.x = outerRadius * Math.cos(smallCircleAngle + segmentAngle);
		ptD.y = outerRadius * Math.sin(smallCircleAngle + segmentAngle);
		ptD.z = 0;

		// Normal (tangent) of circle with radius R at ptD
		var normalD = {x:-ptD.y, y:ptD.x, z:0};

		// Plane?
		// Point E: a point on the circle with radius R
		var ptE = {};

		ptE.x = outerRadius * Math.cos(smallCircleAngle - segmentAngle);
		ptE.y = outerRadius * Math.sin(smallCircleAngle - segmentAngle);
		ptE.z = 0;

		// Normal (tangent) of circle with radius R at ptD
		var normalE = {x:-ptE.y, y:ptE.x, z:0};

		// Lines going through Circle radius r
		var ptBList = [];
		
		for (var kk = 0; kk < facesPerSegment; kk++)
		{
			// Adding 'Math.PI' so that the default (initial) seam is inside
			var a = (kk / facesPerSegment) * Math.PI * 2.0 + faceRotation + Math.PI;
			var rca = innerRadius * Math.cos(a);
			var rsa = innerRadius * Math.sin(a);

			var pt = {};
			pt.x = ptC.x + rca * planeUnitU.x + rsa * planeUnitV.x;
			pt.y = ptC.y + rca * planeUnitU.y + rsa * planeUnitV.y;
			pt.z = ptC.z + rca * planeUnitU.z + rsa * planeUnitV.z;

			var ptA = Math3D.LinePlaneIntersection({pt:pt, v:normalC}, {pt:ptD, n:normalD});
			var ptB = Math3D.LinePlaneIntersection({pt:pt, v:normalC}, {pt:ptE, n:normalE});

			// We need one set of lines to unwrap
			if (j == 0)
				linesS.push({ptA:ptA, ptB:ptB});

			fullGfxPath.MoveTo(ptA);
			fullGfxPath.LineTo(ptB);
			
			ptBList.push(ptB);
		}
		
		// Draw the 'ring' 
		for (var kk = 0; kk < ptBList.length; kk++)
		{
			if (kk == 0)
				fullGfxPath.MoveTo(ptBList[kk]);
			else
				fullGfxPath.LineTo(ptBList[kk]);
			

		}
		fullGfxPath.Close();
		
		// If we are only drawing one segment, then we need to draw the other end now
		for (var kk = 0; (kk < ptBList.length) && (this.settings.showOneSection) && (j == 0); kk++)
		{
			if (kk == 0)
				fullGfxPath.MoveTo(linesS[kk].ptA);
			else
				fullGfxPath.LineTo(linesS[kk].ptA);
		}
		fullGfxPath.Close();
	}

	if (this.settings.showTorus)
		this.gfxDrawing.AddPath(fullGfxPath, { strokePath:true, strokeColor:"#00ffff", strokeWidth:1.0} );

	
	// Unwrap the lines from one section
	var mappedLines = [];

	// Implement "overlapped face" by simply wrapping around the end of the list back to the beginning
	var count = linesS.length;
	if (this.settings.addOverlap)
		count++;
		
	for (var kk = 0; kk < count; kk++)
	{
		// Four corners of one side of the slices.
		// These are coplanar and form a trapezoid
		var ptA = linesS[kk % linesS.length].ptA;
		var ptB = linesS[kk % linesS.length].ptB;
		var ptC = linesS[(kk + 1) % linesS.length].ptB;
		var ptD = linesS[(kk + 1) % linesS.length].ptA;
		
		// The mid points of the two parallel lines (e.g, the top and bottom lines of the trapezoid)
		var ptABmid = {x:(ptA.x+ptB.x)/2, y:(ptA.y+ptB.y)/2, z:(ptA.z+ptB.z)/2};
		var ptCDmid = {x:(ptC.x+ptD.x)/2, y:(ptC.y+ptD.y)/2, z:(ptC.z+ptD.z)/2};
		
		// The "middle" of the trapezoid, which we will consider the "origin"
		var ptMid =  {x:(ptABmid.x+ptCDmid.x)/2, y:(ptABmid.y+ptCDmid.y)/2, z:(ptABmid.z+ptCDmid.z)/2};
		
		// Normal vectors in the plane of the trapezoid
		var normalU = Math3D.UnitVec3(Math3D.SubVec3Vec3(ptB,ptA));
		var normalV = Math3D.UnitVec3(Math3D.SubVec3Vec3(ptCDmid,ptABmid));
		
		// Compute the corners with respect to the origin
		var offset;
		offset = Math3D.SubVec3Vec3(ptA, ptMid);
		var mappedA = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
		offset = Math3D.SubVec3Vec3(ptB, ptMid);
		var mappedB = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
		offset = Math3D.SubVec3Vec3(ptC, ptMid);
		var mappedC = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
		offset = Math3D.SubVec3Vec3(ptD, ptMid);
		var mappedD = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
		// Identify which mappedLines entry is the overlapped piece. This should not get tabs.
		let isOverlappedPiece = (kk >= linesS.length);
		
		mappedLines.push({ptA:mappedA, ptB:mappedB, ptC:mappedC, ptD:mappedD, overlapped:isOverlappedPiece});
		
	}
	
	// Unfolded slice
	var mapOrigin = {x:0, y:0};
	
	// Find total height, so we can center it 
	// Note that we are not adding the height of the last segment as a shortcut
	// to account for the fact that each segment is given with negative and positive coordinates
	var totalHeight = 0;
	var maxWidth = 0;
	for (var kk = 0; kk < mappedLines.length - 1; kk++)
	{
		totalHeight += mappedLines[kk].ptD.y - mappedLines[kk].ptA.y;
		var w = Math.abs(mappedLines[kk].ptB.x - mappedLines[kk].ptA.x);
		if (maxWidth < w)
			maxWidth = w;
	}
	mapOrigin.y = -totalHeight/2;
	mapOrigin.x = -maxWidth/2 - outerRadius - innerRadius - 10;

	// Change the coordinates of all the slices
	for (var kk = 0; kk < mappedLines.length; kk++)
	{
		var m = mappedLines[kk % mappedLines.length];
		m.ptA.x += mapOrigin.x;
		m.ptA.y += mapOrigin.y;

		m.ptB.x += mapOrigin.x;
		m.ptB.y += mapOrigin.y;

		m.ptC.x += mapOrigin.x;
		m.ptC.y += mapOrigin.y;

		m.ptD.x += mapOrigin.x;
		m.ptD.y += mapOrigin.y;
		
		var yShift = m.ptD.y - m.ptA.y;
		mapOrigin.y += yShift;
	}


	if (0 /* show unfolded segment */)
	{
		var mappedGfxPath = new GfxPath();
		for (var kk = 0; kk < mappedLines.length; kk++)
		{
			var m = mappedLines[kk % mappedLines.length];
			mappedGfxPath.MoveTo(m.ptA);
			mappedGfxPath.LineTo(m.ptB);
			mappedGfxPath.LineTo(m.ptC);
			mappedGfxPath.LineTo(m.ptD);
			mappedGfxPath.Close();
		}
		this.gfxDrawing.AddPath(mappedGfxPath, { strokePath:true, strokeColor:"#0000ff", strokeWidth:2.0} );
	}

	if (0 && this.settings.showCutAndScore)
	{
		// Unfolded cut and score
		var cutGfxPath = new GfxPath();
		var scoreGfxPath = new GfxPath();
	
		// Cut line(s)
		cutGfxPath.MoveTo(mappedLines[0].ptA);
		for (var kk = 0; kk < mappedLines.length; kk++)
			cutGfxPath.LineTo(mappedLines[kk].ptB);
		cutGfxPath.LineTo(mappedLines[mappedLines.length-1].ptC);
		for (var kk = mappedLines.length - 1; kk >= 0; kk--)
			cutGfxPath.LineTo(mappedLines[kk].ptD);
		cutGfxPath.Close();

		// Score lines
		for (var kk = 0; kk < mappedLines.length - 1; kk++)
		{
			var m = mappedLines[kk];
			scoreGfxPath.MoveTo(m.ptC);
			scoreGfxPath.LineTo(m.ptD);
		}

		this.gfxDrawing.AddPath(scoreGfxPath, { strokePath:true, strokeColor:"#c0c0c0", strokeWidth:1.0} );
		this.gfxDrawing.AddPath(cutGfxPath, { strokePath:true, strokeColor:"#ff0000", strokeWidth:1.0} );
	}
	
	// Section outline polygon
	var sectionPolygon = undefined;
	if (this.settings.addSectionOutline)
	{
		sectionPolygon = [];
		let count = linesS.length;
		function cpPt(pt) { return {x:pt.x, y:pt.y} }
		
		sectionPolygon.push(cpPt(mappedLines[0].ptA));
		
		for (var kk = 0; kk < count; kk++)
			sectionPolygon.push(cpPt(mappedLines[kk].ptB));
			
		sectionPolygon.push(cpPt(mappedLines[count-1].ptC));
		
		for (var kk = count - 1; kk >= 0; kk--)
			sectionPolygon.push(cpPt(mappedLines[kk].ptD));
	}

	// Panel outline polygon
	var panelOutlinePolygonList = undefined;
	if (this.settings.addPanelOutline)
	{
		panelOutlinePolygonList = new PolygonList();
		
		for (var kk = 0; kk < linesS.length; kk++)
		{
			var m = mappedLines[kk];
			var poly = [{x:m.ptA.x, y:m.ptA.y}, {x:m.ptB.x, y:m.ptB.y}, {x:m.ptC.x, y:m.ptC.y}, {x:m.ptD.x, y:m.ptD.y}];
			panelOutlinePolygonList.AddPolygonPoints(poly);
		}
	}


	// Construct a collection of edges, identifying each type of edge
	var edgeCutList = [];
	var edgeScoreList = [];
	var edge;
	
	// Top seam
	edge = {ptA:mappedLines[0].ptA, ptB:mappedLines[0].ptB, type:"seamTop"};
	edgeCutList.push(edge);
	
	// Right edge
	for (var kk = 0; kk < mappedLines.length; kk++)
	{
		let edgeType = !mappedLines[kk].overlapped ? "rightCut" : "leftOverlap";
		edge = {ptA:mappedLines[kk].ptB, ptB:mappedLines[kk].ptC, type:edgeType};
		edgeCutList.push(edge);
	}
	
	// Bottom seam
	edge = {ptA:mappedLines[mappedLines.length-1].ptC, ptB:mappedLines[mappedLines.length-1].ptD, type:"seamBottom"};
	edgeCutList.push(edge);

	// Left edge
	for (var kk = mappedLines.length - 1; kk >= 0; kk--)
	{
		let edgeType = !mappedLines[kk].overlapped ? "leftCut" : "leftOverlap";
		edge = {ptA:mappedLines[kk].ptD, ptB:mappedLines[kk].ptA, type:edgeType};
		edgeCutList.push(edge);
	}

	// Score lines
	for (var kk = 0; kk < mappedLines.length - 1; kk++)
	{
		edge = {ptA:mappedLines[kk].ptC, ptB:mappedLines[kk].ptD, type:"score"};
		edgeScoreList.push(edge);
	}
	
	// Section outline
	var sectionOutlinePolygonList = undefined;
	if (this.settings.addSectionOutline)
	{
		sectionOutlinePolygonList = new PolygonList();
		sectionOutlinePolygonList.AddPolygonPoints(sectionPolygon);
	}
	
	if (sectionCount > 1)
	{
		// Original edge list
		var originalEdgeCutList = JSON.parse(JSON.stringify(edgeCutList));
		var originalEdgeScoreList = JSON.parse(JSON.stringify(edgeScoreList));
		
		// Identify the cut edge from each list that will be combined into
		// a score edge
		var commonFace = this.settings.sectionConnect % facesPerSegment;
		
		// If we are combining multiple cut-and-score sections, then identify which
		// edges are being combined into a score edge.
		// The "right" will refer to the copied edgeList.
		// This 'rightEdgeListIdx' will not change, since we are always concatenating a copy
		// of the original edge list
		var rightEdgeListIdx = facesPerSegment * 2 + 1 - commonFace;
		
		// If we added an extra overlapped 'panel', then adjust the rightEdgeListIdx value
		if (this.settings.addOverlap)
			rightEdgeListIdx += 2;
		
		// The "left" will refer to the original and combined edgeList. 
		// This 'leftEdgeListIdx' value will change for each iteration
		var leftEdgeListIdx = commonFace + 1;
	
		// Combine copies of the unwrapped section
		for (var jj = 1; jj < sectionCount; jj++)
		{
			// Make a copy of the edges
			var edgeCutCopy = JSON.parse(JSON.stringify(originalEdgeCutList));
			var edgeScoreCopy = JSON.parse(JSON.stringify(originalEdgeScoreList));

			// Calculate the transform 
			var eLeft = edgeCutList[leftEdgeListIdx];
			var eRight = JSON.parse(JSON.stringify(edgeCutCopy[rightEdgeListIdx]));
			var angle = MathUtil.CalcAngleBetweenLines(eLeft, {ptA:eRight.ptB, ptB:eRight.ptA});
	
			var mT1 = MathUtil.TranslateMat({x:-eRight.ptB.x, y:-eRight.ptB.y});
			var mR  = MathUtil.RotateMat(-angle);
			var mT2 = MathUtil.TranslateMat(eLeft.ptA)
			var m = MathUtil.MultMatList([mT2, mR, mT1]);			
			
			// Rotate and translate function
			function mapEdge(e)	{
				e.ptA = MathUtil.MultMatPt(m, e.ptA);
				e.ptB = MathUtil.MultMatPt(m, e.ptB);
			}
	
			// Apply the rotate and translate function to align the common edges
			edgeCutCopy.forEach(mapEdge);
			edgeScoreCopy.forEach(mapEdge);

			// Copy and translate the section outline
			if (this.settings.addSectionOutline)
			{
				var poly = [];
				sectionPolygon.forEach(pt => poly.push(MathUtil.MultMatPt(m, pt)));
				sectionOutlinePolygonList.AddPolygonPoints(poly);
			}
	
			// Combine the score lists
			var combinedScore = edgeScoreList.concat(edgeScoreCopy);
			
			// Walk around the edge lists and construct a combined edgelist

			var nextLeftEdgeIdx = undefined;
			var combinedCut = [];
			var e;
			for (var i = leftEdgeListIdx + 1; i < edgeCutList.length; i++)
			{
				e = edgeCutList[i];
				combinedCut.push(e);
			}
			for (var i = 0; i < leftEdgeListIdx; i++)
			{
				e = edgeCutList[i];
				combinedCut.push(e);
			}
			e = edgeCutList[leftEdgeListIdx];
			e.type = "score";
			combinedScore.push(e);
			
			for (var i = rightEdgeListIdx + 1; i < edgeCutCopy.length; i++)
			{
				e = edgeCutCopy[i];
				combinedCut.push(e);
			}
			nextLeftEdgeIdx = combinedCut.length + commonFace + 1;
			for (var i = 0; i < rightEdgeListIdx; i++)
			{
				e = edgeCutCopy[i];
				combinedCut.push(e);
			}
			leftEdgeListIdx = nextLeftEdgeIdx
			
			
			// Identify the edges that are newly paired, which will be the right edges from
			// the left piece and the left edges from the right piece
			var pairList = [];
			for (var i = 0; i < commonFace; i++)
			{
				let idxA = edgeCutList.length - 1 - 1 - i; // Right edge from left piece 
				let idxB = edgeCutList.length - 1 + i; // Left edge from right piece
				pairList.push({idxA:idxA, idxB:idxB});
				//console.log(combinedCut[idxA].type, "-->", combinedCut[idxB].type);
			}
			//console.log(" -- common -- ");
			for (var i = commonFace + 1; i < facesPerSegment; i++)
			{
				let n = i - (commonFace + 1); // Starts at 0
				let idxA = n; // Right edge from left piece
				let idxB = combinedCut.length - 1 - n 
				pairList.push({idxA:idxA, idxB:idxB});
				//console.log(combinedCut[idxA].type, "-->", combinedCut[idxB].type);
			}

			// Replace the edge list with the combined edge list
			edgeCutList = combinedCut;
			edgeScoreList = combinedScore;
			
			// Analyze the paired edges to determine if the slots or tabs overlap
			for (var i = 0; i < pairList.length; i++)
			{
				let p = pairList[i];
				let dA = MathUtil.DistanceBetween(edgeCutList[p.idxA].ptA, edgeCutList[p.idxB].ptB); 
				let dB = MathUtil.DistanceBetween(edgeCutList[p.idxB].ptA, edgeCutList[p.idxA].ptB); 
				let d = (dA < dB) ? dA : dB;
				
				if (d < this.settings.edgeTabDepth)
				{
					edgeCutList[p.idxA].type += "Narrow";
					edgeCutList[p.idxB].type += "Narrow";
				}
			}
		}
	}
	
	// Create unfolded score path
	var scoreGfxPath = new GfxPath();
	for (var kk = 0; kk < edgeScoreList.length; kk++)
	{
		var e = edgeScoreList[kk];
		
		if (e.type == "score")
		{
			scoreGfxPath.MoveTo(e.ptA);
			scoreGfxPath.LineTo(e.ptB);
		}
	}
	
	// Create unfolded cut path
	var cutGfxPath = new GfxPath();
	var slotCutGfxPath = new GfxPath();
	var infoGfxPath = undefined;
	if (0)
		infoGfxPath = new GfxPath();
		
	var startPath = true;

	var cutConfig = this.GetEdgeConfig();
		
	// It does not make sense to have tabs or slots if there in an overlapped panel
	if (this.settings.addOverlap)
		cutConfig.seamTabStyle = "cut";
	
	for (var kk = 0; kk < edgeCutList.length; kk++)
	{
		var e = edgeCutList[kk];
		
		if (e.type != "score")
		{
			if (startPath)
			{
				cutGfxPath.MoveTo(e.ptA);
				startPath = false;
			}

			this.AddCutEdge({cutPath:cutGfxPath, scorePath:scoreGfxPath, slotCutPath:slotCutGfxPath, debugPath:infoGfxPath}, e, cutConfig);
		}
	}
	cutGfxPath.Close();

	cutGfxPath.Append(slotCutGfxPath);
	
	// Convert the section outline polygon list into a gfx path
	var sectionOutlineGfxPath = undefined;
	if (this.settings.addSectionOutline)
		sectionOutlineGfxPath = PolygonListToGfxPath(sectionOutlinePolygonList, this.settings.sectionOutlineInset);

	// Convert the panel outline polygon list into a gfx path
	var panelOutlineGfxPath = undefined;
	if (this.settings.addPanelOutline)
		panelOutlineGfxPath = PolygonListToGfxPath(panelOutlinePolygonList, this.settings.panelOutlineInset);

	if (this.settings.showCutAndScore)
	{
		//this.gfxDrawing.AddPath(slotCutGfxPath, { strokePath:true, strokeColor:"#000080", strokeWidth:0.5} );
		this.gfxDrawing.AddPath(cutGfxPath, { strokePath:true, strokeColor:"#8080f0", strokeWidth:0.5} );

		this.gfxDrawing.AddPath(scoreGfxPath, { strokePath:true, strokeColor:"#d0d0d0", strokeWidth:0.5} );

		if (panelOutlineGfxPath != undefined)
			this.gfxDrawing.AddPath(panelOutlineGfxPath, { strokePath:true, strokeColor:"#8888ff", strokeWidth:0.25} );

		if (sectionOutlineGfxPath != undefined)
			this.gfxDrawing.AddPath(sectionOutlineGfxPath, { strokePath:true, strokeColor:"#88ff88", strokeWidth:0.25} );

		if (infoGfxPath != undefined)
			this.gfxDrawing.AddPath(infoGfxPath, { strokePath:true, strokeColor:"#ff0000", strokeWidth:0.5} );
	}
	
	// Sizes
	this.MeasureSizes({cutPath:cutGfxPath, solidPath:fullGfxPath});
}


//-------------------------------------------------------------------------------------
//	Public
//-------------------------------------------------------------------------------------
export { FoldableSectionsTorusGfxApp };


//-------------------------------------------------------------------------------------
//
//	Foldable Sections Solid
//
//		Create plans for foldable sections for a torus
//
//-------------------------------------------------------------------------------------


//-------------------------------------------------------------------------------------
//	Constructor
//-------------------------------------------------------------------------------------
function FoldableSectionsSolidGfxApp()
{
	FoldableSectionsGfxApp.call(this, 
		"Foldable Solid Frame Designer", 
		"FoldableSectionsSolidGfxAppId",
		FoldableSectionsSolidGfxApp.defaultSettings, 
		FoldableSectionsSolidGfxApp.controlGroups);
}

FoldableSectionsSolidGfxApp.prototype = Object.create(FoldableSectionsGfxApp.prototype);

Object.defineProperty(FoldableSectionsSolidGfxApp.prototype, 'constructor', {
	value: FoldableSectionsSolidGfxApp,
	enumerable: false,
	write: true });


//-------------------------------------------------------------------------------------
//	Default settings
//-------------------------------------------------------------------------------------
FoldableSectionsSolidGfxApp.defaultSettings = 
	{
		solid:				"cube",
		solidRadius:		100,
		innerRadius:		20,
		facesPerSegment:	5,
		alignEdgeFlag:		true,
		seamLocation:		0,
		angleU: 0,
		showSolidShape:		false,
		showCutAndScore:	true,
		showTabSafeArea:	false,
		showRenderedSolid:	true,
		showOneSection:		false,
		multiColor:			false,
		addOverlap:			false,
	}


//-------------------------------------------------------------------------------------
//	Controls and Control Groups
//-------------------------------------------------------------------------------------
FoldableSectionsSolidGfxApp.solidSelection = 
	[
		{ label: "Tetrahedron",		value: "tetra" },
		{ label: "Cube",			value: "cube" },
		{ label: "Octahedron",		value: "octa" },
		{ label: "Dodecahedron",	value: "dodeca" },
		{ label: "Icosahedron",		value: "icosa" },
		{ label: "Triacontahedron",	value: "triaconta" }
	];

FoldableSectionsSolidGfxApp.generalControls =
	[
		{ label: "Solid", 			type: "selection",	prop: "solid",		options: FoldableSectionsSolidGfxApp.solidSelection	},
		{ label: "Size",			type: "number",		prop: "solidRadius",			min:1, max: 200, step:1, entry:"yes"	},
		{ label: "Radius2",			type: "number",		prop: "innerRadius",			min:1, max: 200, step:1, entry:"yes"	},
		{ label: "Faces",			type: "number",		prop: "facesPerSegment",		min:3, max: 24, step:1, entry:"yes"	},
		{ label: "Seam location",	type: "number",		prop: "seamLocation",			min:0, max: 24, step:1, entry:"yes"	},
		{ label: "Align Edge",		type: "boolean",	prop: "alignEdgeFlag"	},
	];

FoldableSectionsSolidGfxApp.displayControls =
	[
		{ label: "Show basic form",					type: "boolean",	prop: "showSolidShape"			},
		{ label: "Show shape",						type: "boolean",	prop: "showRenderedSolid"	 	},
		{ label: "&nbsp;&nbsp;Multiple colors",		type: "boolean",	prop: "multiColor"				},
		{ label: "&nbsp;&nbsp;Show one section",	type: "boolean",	prop: "showOneSection"	 		},
		{ label: "Show cut and score",				type: "boolean",	prop: "showCutAndScore"			},
		{ label: "&nbsp;&nbsp;Show tab safe area",	type: "boolean",	prop: "showTabSafeArea"			}
	];
	
FoldableSectionsSolidGfxApp.sectionControls =
	[
		{ label: "Add overlap face",		type: "boolean",	prop: "addOverlap"		 		},
	];
	
FoldableSectionsSolidGfxApp.controlGroups = 
	[
		{ label: "Shape",			controls: FoldableSectionsSolidGfxApp.generalControls	},
		{ label: "Display",			controls: FoldableSectionsSolidGfxApp.displayControls	},
		{ label: "Section",			controls: FoldableSectionsSolidGfxApp.sectionControls	},
		{ label: "Tabs",			controls: FoldableSectionsGfxApp.tabAndSlotControls		}
	];

//-------------------------------------------------------------------------------------
//	GetSolid
//-------------------------------------------------------------------------------------
FoldableSectionsSolidGfxApp.prototype.GetSolid = function(name)
{
	var solid;
	
	if (name == "tetra")
		solid = Shapes3D.UnitTetrahedron(); 
	else if (name == "cube")
		solid = Shapes3D.UnitCube(); 
	else if (name == "octa")
		solid = Shapes3D.UnitOctahedron(); 
	else if (name == "dodeca")
		solid = Shapes3D.UnitDodecahedron(); 
	else if (name == "icosa")
		solid = Shapes3D.UnitIcosahedron(); 
	else if (name == "triaconta")
		solid = Shapes3D.UnitTriacontahedron(); 
	else
		solid = Shapes3D.UnitCube(); 

	return solid;
}

//-------------------------------------------------------------------------------------
//	Generate
//-------------------------------------------------------------------------------------
FoldableSectionsSolidGfxApp.prototype.Generate = function()
{
	this.gfxDrawing = new GfxDrawing3D();

	var solidRadius      = this.settings.solidRadius;
	var innerRadius      = this.settings.innerRadius;
	var facesPerSegment  = this.settings.facesPerSegment;
	var alignEdgeFlag    = this.settings.alignEdgeFlag;
	var seamLocation     = this.settings.seamLocation;
	
	// Insure value are reasonable
	if (facesPerSegment < 3)
		facesPerSegment = 3;
		
	seamLocation = (seamLocation % facesPerSegment);
	
	// Get the solid and scale
	var solid = this.GetSolid(this.settings.solid);
	solid.points.forEach(p => {p.x *= solidRadius; p.y *= solidRadius; p.z *= solidRadius});
	
	
	// Create an edges array
	var edges = [];
	for (var kk = 0; kk < solid.edges.length; kk++)
	{
		var se = solid.edges[kk];
		var e = {};
		
		// Add the end points of the edge
		e.ptA = solid.points[se[0]];
		e.ptB = solid.points[se[1]];
		
		// Calculate the unit vector and 'normal' vector for the edge
		e.unitV = Math3D.UnitVec3(Math3D.SubVec3Vec3(e.ptB, e.ptA));
		e.normV = Math3D.UnitVec3(Math3D.InterpolateVec3Vec3(e.ptA, e.ptB, 0.5));
		
		function findLeftEdge(ptIdx, idx, face)
		{
			let nextPtIdx = face[(idx + 1) % face.length];
			return (ptIdx == se[0] && nextPtIdx == se[1]);
		}
		function findRightEdge(ptIdx, idx, face)
		{
			let nextPtIdx = face[(idx + 1) % face.length];
			return (ptIdx == se[1] && nextPtIdx == se[0]);
		}
		// Find the faces on each side of the edge
		//console.log("edge", kk, "  ", se[0], "-->", se[1]);
		for (var jj = 0; jj < solid.faces.length; jj++)
		{
			var f = solid.faces[jj];
			var leftIdx = f.findIndex(findLeftEdge);
			var rightIdx = f.findIndex(findRightEdge);
			
			let l = f.length;
			if (leftIdx != -1)
			{
				let ptCIdx = f[(leftIdx + 2    ) % l];
				let ptEIdx = f[(leftIdx - 1 + l) % l];
				
				e.ptC = solid.points[ptCIdx];
				e.ptE = solid.points[ptEIdx];
			}
			if (rightIdx != -1)
			{
				let ptDIdx = f[(rightIdx - 1 + l) % l];
				let ptFIdx = f[(rightIdx + 2    ) % l];
				
				e.ptD = solid.points[ptDIdx];
				e.ptF = solid.points[ptFIdx];
			}
		}
		
		edges.push(e);
	}
	
	for (var kk = 0; kk < edges.length; kk++)
	{
		var e = edges[kk];
	
		e.planeUnitU = e.normV;
		e.planeUnitV = Math3D.CrossProduct(e.unitV, e.planeUnitU);
		Math3D.ToUnitVec3(e.planeUnitV);
	}
	
	// Draw the edges of the underlying shape
	if (this.settings.showSolidShape)
	{
		var edgeGfxPath = new GfxPath3D();
		for (var kk = 0; kk < solid.edges.length; kk++)
		{
			edgeGfxPath.MoveTo(edges[kk].ptA);
			edgeGfxPath.LineTo(edges[kk].ptB);
		}
	
		this.gfxDrawing.AddPath(edgeGfxPath, { strokePath:true, strokeColor:"#00ff00", strokeWidth:2} );
	}

	
	var colors = this.settings.multiColor ? ["#ff8080", "#80ff80", "#8080ff", "#dddd80", "#80ffff", "#ff80ff"] : ["#4040ff"];
	var linesS;
	var edgesToRender = (this.settings.showOneSection ? 1 : edges.length);
	var solidBounds = undefined;
	for (var jj = 0; jj < edgesToRender; jj++)
	{
		var parallelsGfxPath = new GfxPath3D();
		var e = edges[jj];
		
		// Calculate a plane that bisects the angle defined by three points
		let planeC = Math3D.CornerBisectPlane(e.ptC, e.ptB, e.ptA);
		let planeD = Math3D.CornerBisectPlane(e.ptD, e.ptB, e.ptA);
	

		let planeE = Math3D.CornerBisectPlane(e.ptE, e.ptA, e.ptB);
		let planeF = Math3D.CornerBisectPlane(e.ptF, e.ptA, e.ptB);

		var linesP = [];
		let angleIncrement = Math.PI * 2.0 / facesPerSegment;
		let angleOffset = (alignEdgeFlag) ? 0 : angleIncrement / 2;
		
		// Set the default seam position to be on the inside.
		// Note that for odd facesPerSegment, we can't simply add 180 degrees, we have 
		// to account for that a 180 degree rotation effectively moves a face to an edge
		angleOffset += Math.PI - ((facesPerSegment % 2 == 0) ? 0 : angleIncrement/2);
		
		for (var kk = 0; kk < facesPerSegment; kk++)
		{
			// Calculate vectors that point to locations around a circle
			var a = (kk + seamLocation) * angleIncrement + angleOffset;
			var rca = innerRadius * Math.cos(a);
			var rsa = innerRadius * Math.sin(a);
	
			// Calculate a point on the circle in a plane perpendicular to the edge positioned
			// at the mid point (i.e., not at the corner) 
			var midPt = Math3D.InterpolateVec3Vec3(e.ptA, e.ptB, 0.5);
			var pt = {};
			pt.x = midPt.x + rca * e.planeUnitU.x + rsa * e.planeUnitV.x;
			pt.y = midPt.y + rca * e.planeUnitU.y + rsa * e.planeUnitV.y;
			pt.z = midPt.z + rca * e.planeUnitU.z + rsa * e.planeUnitV.z;
	
			// Find where the line intersects each plane and Select the point that is closer
			var ptA;
			var testPtC = Math3D.LinePlaneIntersection({pt:pt, v:e.unitV}, planeC );
			var testPtD = Math3D.LinePlaneIntersection({pt:pt, v:e.unitV}, planeD );
			let distanceTestC = Math3D.LenVec3(Math3D.SubVec3Vec3(testPtC, pt));
			let distanceTestD = Math3D.LenVec3(Math3D.SubVec3Vec3(testPtD, pt));
			ptA = (distanceTestC < distanceTestD) ? testPtC : testPtD;
			
			var ptB;
			var testPtE = Math3D.LinePlaneIntersection({pt:pt, v:e.unitV}, planeE );
			var testPtF = Math3D.LinePlaneIntersection({pt:pt, v:e.unitV}, planeF );
			let distanceTestE = Math3D.LenVec3(Math3D.SubVec3Vec3(testPtE, pt));
			let distanceTestF = Math3D.LenVec3(Math3D.SubVec3Vec3(testPtF, pt));
			ptB = (distanceTestE < distanceTestF) ? testPtE : testPtF;
	
			linesP.push({ptA:JSON.parse(JSON.stringify(ptA)), ptB:JSON.parse(JSON.stringify(ptB)), type:"edge"});

			parallelsGfxPath.MoveTo(ptA);
			parallelsGfxPath.LineTo(ptB);
		
			// Determine if we are about to cross a multiple Pi
			var aa = a;
			while (aa >= Math.PI)
				aa -= Math.PI;
			if (aa > epsilon && aa + angleIncrement > Math.PI + epsilon)
			{
				// Compute the next multiple of Pi
				var aOffset = Math.PI - aa;
				var a2 = a + aOffset;
			
				if (aOffset > angleIncrement/2)
					aOffset = angleIncrement - aOffset;

				var r2 = innerRadius * Math.cos(aOffset);
				var rca = r2 * Math.cos(a2);
				var rsa = r2 * Math.sin(a2);
	
				// Calculate a point on the circle in a plane perpendicular to the edge positioned
				// at the end point (i.e., not at the corner) 
				var midPt = Math3D.InterpolateVec3Vec3(e.ptA, e.ptB, 0.5);
				var pt = {};
				pt.x = midPt.x + rca * e.planeUnitU.x + rsa * e.planeUnitV.x;
				pt.y = midPt.y + rca * e.planeUnitU.y + rsa * e.planeUnitV.y;
				pt.z = midPt.z + rca * e.planeUnitU.z + rsa * e.planeUnitV.z;
	
				// Find where the line intersects each plane and Select the point that is closer
				var ptA;
				var testPtC = Math3D.LinePlaneIntersection({pt:pt, v:e.unitV}, planeC );
				var testPtD = Math3D.LinePlaneIntersection({pt:pt, v:e.unitV}, planeD );
				let distanceTestC = Math3D.LenVec3(Math3D.SubVec3Vec3(testPtC, pt));
				let distanceTestD = Math3D.LenVec3(Math3D.SubVec3Vec3(testPtD, pt));
				ptA = (distanceTestC < distanceTestD) ? testPtC : testPtD;
			
				var ptB;
				var testPtE = Math3D.LinePlaneIntersection({pt:pt, v:e.unitV}, planeE );
				var testPtF = Math3D.LinePlaneIntersection({pt:pt, v:e.unitV}, planeF );
				let distanceTestE = Math3D.LenVec3(Math3D.SubVec3Vec3(testPtE, pt));
				let distanceTestF = Math3D.LenVec3(Math3D.SubVec3Vec3(testPtF, pt));
				ptB = (distanceTestE < distanceTestF) ? testPtE : testPtF;
	
				linesP.push({ptA:JSON.parse(JSON.stringify(ptA)), ptB:JSON.parse(JSON.stringify(ptB)), type:"corner"});
			}
		}

		for (var kk = 0; kk < linesP.length; kk++)
		{
			parallelsGfxPath.MoveTo(linesP[kk].ptA);
			parallelsGfxPath.LineTo(linesP[(kk + 1) % linesP.length].ptA);

			parallelsGfxPath.MoveTo(linesP[kk].ptB);
			parallelsGfxPath.LineTo(linesP[(kk + 1) % linesP.length].ptB);
	
		}
		if (this.settings.showRenderedSolid)
			this.gfxDrawing.AddPath(parallelsGfxPath, { strokePath:true, strokeColor:colors[jj % colors.length], strokeWidth:1.5} );

		// Copy the first set of lines for unwrapping
		if (linesS == undefined)
				linesS = JSON.parse(JSON.stringify(linesP));
				
		// Accumulate bounds
		solidBounds = parallelsGfxPath.FindBounds(solidBounds);
	}
	
	var mappedLines = [];

	var kk = 0;
	var count = linesS.length;
	
	// Implement "overlapped face" by simply wrapping around the end of the list back to the beginning
	if (this.settings.addOverlap)
		count++;
		
	while (kk < count)
	{
		// Four corners of one side of the slices.
		// These are coplanar.
		// For some shapes, there do NOT form an isosceles trapezoid
		var ptA = linesS[kk % linesS.length].ptA;
		var ptB = linesS[kk % linesS.length].ptB;
		
		var nxt = 1;
		if (linesS[(kk + nxt) % linesS.length].type == "corner")
		{
			var ptE = linesS[(kk + nxt) % linesS.length].ptB;
			var ptF = linesS[(kk + nxt) % linesS.length].ptA;
			nxt++;
		}

		var ptC = linesS[(kk + nxt) % linesS.length].ptB;
		var ptD = linesS[(kk + nxt) % linesS.length].ptA;
		
		// The mid points of the two parallel lines (e.g, the top and bottom lines of the trapezoid)
		var ptABmid = {x:(ptA.x+ptB.x)/2, y:(ptA.y+ptB.y)/2, z:(ptA.z+ptB.z)/2};
		var ptCDmid = {x:(ptC.x+ptD.x)/2, y:(ptC.y+ptD.y)/2, z:(ptC.z+ptD.z)/2};
		
		// xx -- The "middle" of the trapezoid, which we will consider the "origin"
		// xx -- var ptMid =  {x:(ptABmid.x+ptCDmid.x)/2, y:(ptABmid.y+ptCDmid.y)/2, z:(ptABmid.z+ptCDmid.z)/2};
		
		// The "origin" of the trapezoid will be the bottom center
		var ptMid = {x:ptCDmid.x, y:ptCDmid.y, z:ptCDmid.z};
		
		// Normal vectors in the plane of the trapezoid
		var normalU = Math3D.UnitVec3(Math3D.SubVec3Vec3(ptB,ptA));
		//var normalV = Math3D.UnitVec3(Math3D.SubVec3Vec3(ptCDmid,ptABmid));
		var tmp1 = Math3D.SubVec3Vec3(ptCDmid,ptABmid);
		var trapezoidPlaneNormal = Math3D.CrossProduct(normalU, tmp1);
		var normalV = Math3D.UnitVec3(Math3D.CrossProduct(normalU, trapezoidPlaneNormal));
		
		try {
		
			// Compute the corners with respect to the origin
			var offset;
			offset = Math3D.SubVec3Vec3(ptA, ptMid);
			var mappedA = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
			offset = Math3D.SubVec3Vec3(ptB, ptMid);
			var mappedB = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
			offset = Math3D.SubVec3Vec3(ptC, ptMid);
			var mappedC = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
			offset = Math3D.SubVec3Vec3(ptD, ptMid);
			var mappedD = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
			var trapezoid = {ptA:mappedA, ptB:mappedB, ptC:mappedC, ptD:mappedD};
		
			if (nxt == 2)
			{
				offset = Math3D.SubVec3Vec3(ptE, ptMid);
				var mappedE = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
		
				offset = Math3D.SubVec3Vec3(ptF, ptMid);
				var mappedF = { x:Math3D.DotProduct(offset, normalU), y:Math3D.DotProduct(offset, normalV) };
			
				trapezoid.ptE = mappedE;
				trapezoid.ptF = mappedF;
			}
			
			// Identify which mappedLines entry is the overlapped piece. This should not get tabs.
			let isOverlappedPiece = (kk >= linesS.length);
			trapezoid.overlapped = isOverlappedPiece;
		
			mappedLines.push(trapezoid);
		}
		catch (err) {
			console.error(err);
			console.log("kk",kk, "linesS.length",linesS.length, "facesPerSegment", facesPerSegment);
		}
		
		kk += nxt;
	}
	
	// Unfolded slice
	var mappedGfxPath = new GfxPath();
	var mapOrigin = {x:0, y:0};
	
	// Find total height, so we can center it 
	// Note that we are not adding the height of the last segment as a shortcut
	// to account for the fact that each segment is given with negative and positive coordinates
	var totalHeight = 0;
	var maxWidth = 0;
	for (var kk = 0; kk < mappedLines.length - 1; kk++)
	{
		totalHeight += mappedLines[kk].ptD.y - mappedLines[kk].ptA.y;
		var w = Math.abs(mappedLines[kk].ptB.x - mappedLines[kk].ptA.x);
		if (maxWidth < w)
			maxWidth = w;
	}
	mapOrigin.y = -totalHeight/2;
	mapOrigin.x = -maxWidth/2 - solidRadius - innerRadius - 20;

	// Change the coordinates of all the slices
	var horzAdj = 0;
	var prevX;
	for (var kk = 0; kk < mappedLines.length; kk++)
	{
		var m = mappedLines[kk % mappedLines.length];
		
		if (kk > 0)
			horzAdj += prevX - m.ptA.x;
		prevX = m.ptD.x;
		
		m.ptA.x += mapOrigin.x + horzAdj;
		m.ptA.y += mapOrigin.y;

		m.ptB.x += mapOrigin.x + horzAdj;
		m.ptB.y += mapOrigin.y;

		m.ptC.x += mapOrigin.x + horzAdj;
		m.ptC.y += mapOrigin.y;

		m.ptD.x += mapOrigin.x + horzAdj;
		m.ptD.y += mapOrigin.y;
		
		if (m.ptE != undefined)
		{
			m.ptE.x += mapOrigin.x + horzAdj;
			m.ptE.y += mapOrigin.y;
			m.ptF.x += mapOrigin.x + horzAdj;
			m.ptF.y += mapOrigin.y;
		}
		
		var yShift = m.ptD.y - m.ptA.y;
		mapOrigin.y += yShift;
	}

	// (Draw the 'panels'. Keep this to verify that the paths, below, are correct.)
	if (0)
	{
		for (var kk = 0; kk < mappedLines.length; kk++)
		{
			var m = mappedLines[kk % mappedLines.length];
			mappedGfxPath.MoveTo(m.ptA);
			mappedGfxPath.LineTo(m.ptB);
			if (m.ptE != undefined)
				mappedGfxPath.LineTo(m.ptE);
			mappedGfxPath.LineTo(m.ptC);
			mappedGfxPath.LineTo(m.ptD);
			if (m.ptF != undefined)
				mappedGfxPath.LineTo(m.ptF);
			mappedGfxPath.Close();
		}
	
		this.gfxDrawing.AddPath(mappedGfxPath, { strokePath:true, strokeColor:"#c0FFc0", strokeWidth:3.0} );
	}

	// Unfolded cut and score
	var cutGfxPath = new GfxPath();
	var scoreGfxPath = new GfxPath();
	
	// Cut line(s)
	cutGfxPath.MoveTo(mappedLines[0].ptA);
	for (var kk = 0; kk < mappedLines.length; kk++)
	{
		cutGfxPath.LineTo(mappedLines[kk].ptB);
		if (mappedLines[kk].ptE != undefined)
			cutGfxPath.LineTo(mappedLines[kk].ptE);
	}
	cutGfxPath.LineTo(mappedLines[mappedLines.length-1].ptC);
	for (var kk = mappedLines.length - 1; kk >= 0; kk--)
	{
		cutGfxPath.LineTo(mappedLines[kk].ptD);
		if (mappedLines[kk].ptF != undefined)
			cutGfxPath.LineTo(mappedLines[kk].ptF);
	}
	cutGfxPath.Close();

	// Score lines
	for (var kk = 0; kk < mappedLines.length - 1; kk++)
	{
		var m = mappedLines[kk];
		scoreGfxPath.MoveTo(m.ptC);
		scoreGfxPath.LineTo(m.ptD);
	}

	if (0 && this.settings.showCutAndScore)
	{
		this.gfxDrawing.AddPath(scoreGfxPath, { strokePath:true, strokeColor:"#c0c0c0", strokeWidth:1.0} );
		this.gfxDrawing.AddPath(cutGfxPath, { strokePath:true, strokeColor:"#ff0000", strokeWidth:1.0} );
	}

	// Construct a collection of edges, identifying each type of edge
	var edgeCutList = [];
	var edgeScoreList = [];
	var edge;
	var leftRightSwitch = Math.floor((facesPerSegment + 1)/2);
	
	// Top seam
	edge = {ptA:mappedLines[0].ptB, ptB:mappedLines[0].ptA, type:"seamTop"};
	edgeCutList.push(edge);
	
	// Left edge
	for (var kk = 0; kk < mappedLines.length; kk++)
	{
		if (mappedLines[kk].ptF == undefined)
		{
			let edgeType
			
			if (((kk + seamLocation) % mappedLines.length) < leftRightSwitch)
				edgeType = !mappedLines[kk].overlapped ? "leftCut" : "leftOverlap";
			else
				edgeType = !mappedLines[kk].overlapped ? "rightCut" : "rightOverlap";
			edge = {ptA:mappedLines[kk].ptA, ptB:mappedLines[kk].ptD, type:edgeType};
			edgeCutList.push(edge);
		}
		else
		{
			edge = {ptA:mappedLines[kk].ptA, ptB:mappedLines[kk].ptF, type:"corner"};
			edgeCutList.push(edge);

			edge = {ptA:mappedLines[kk].ptF, ptB:mappedLines[kk].ptD, type:"corner"};
			edgeCutList.push(edge);
		}
	}
	// Bottom seam
	edge = {ptA:mappedLines[mappedLines.length-1].ptD, ptB:mappedLines[mappedLines.length-1].ptC, type:"seamBottom"};
	edgeCutList.push(edge);

	// Right edge
	for (var kk = mappedLines.length - 1; kk >= 0; kk--)
	{
		if (mappedLines[kk].ptE == undefined)
		{
			let edgeType;
			if (((kk + seamLocation) % mappedLines.length)  >= leftRightSwitch)
				edgeType = !mappedLines[kk].overlapped ? "leftCut" : "leftOverlap";
			else
				edgeType = !mappedLines[kk].overlapped ? "rightCut" : "rightOverlap";
			edge = {ptA:mappedLines[kk].ptC, ptB:mappedLines[kk].ptB, type:edgeType};
			edgeCutList.push(edge);
		}
		else
		{
			edge = {ptA:mappedLines[kk].ptC, ptB:mappedLines[kk].ptE, type:"corner"};
			edgeCutList.push(edge);

			edge = {ptA:mappedLines[kk].ptE, ptB:mappedLines[kk].ptB, type:"corner"};
			edgeCutList.push(edge);
		}
	}

	// Score lines
	for (var kk = 0; kk < mappedLines.length - 1; kk++)
	{
		edge = {ptA:mappedLines[kk].ptC, ptB:mappedLines[kk].ptD, type:"score"};
		edgeScoreList.push(edge);
	}
	
	// Create unfolded score path
	var scoreGfxPath = new GfxPath();
	for (var kk = 0; kk < edgeScoreList.length; kk++)
	{
		var e = edgeScoreList[kk];
		
		if (e.type == "score")
		{
			scoreGfxPath.MoveTo(e.ptA);
			scoreGfxPath.LineTo(e.ptB);
		}
	}
	
	// Create unfolded cut path
	var cutGfxPath = new GfxPath();
	var slotCutGfxPath = new GfxPath();
	var infoGfxPath = undefined;
	if (this.settings.showTabSafeArea)
		infoGfxPath = new GfxPath();
	var startPath = true;

	var cutConfig = this.GetEdgeConfig();
	
	// It does not make sense to have tabs or slots if there in an overlapped panel
	if (this.settings.addOverlap)
		cutConfig.seamTabStyle = "cut";
	
	for (var kk = 0; kk < edgeCutList.length; kk++)
	{
		var e = edgeCutList[kk];
		
		if (e.type != "score")
		{
			if (startPath)
			{
				cutGfxPath.MoveTo(e.ptA);
				startPath = false;
			}

			this.AddCutEdge({cutPath:cutGfxPath, scorePath:scoreGfxPath, slotCutPath:slotCutGfxPath, debugPath:infoGfxPath}, e, cutConfig);
		}
	}
	cutGfxPath.Close();

	cutGfxPath.Append(slotCutGfxPath);

	if (this.settings.showCutAndScore)
	{
		//this.gfxDrawing.AddPath(slotCutGfxPath, { strokePath:true, strokeColor:"#000080", strokeWidth:0.5} );
		this.gfxDrawing.AddPath(cutGfxPath, { strokePath:true, strokeColor:"#8080f0", strokeWidth:0.5} );
		this.gfxDrawing.AddPath(scoreGfxPath, { strokePath:true, strokeColor:"#d0d0d0", strokeWidth:0.5} );
		if (infoGfxPath != undefined)
			this.gfxDrawing.AddPath(infoGfxPath, { strokePath:true, strokeColor:"#ff0000", strokeWidth:0.25} );
	}

	// Sizes
	this.MeasureSizes({cutPath:cutGfxPath, solidBounds:solidBounds, sectionCount:edges.length});
}

//-------------------------------------------------------------------------------------
//	Public
//-------------------------------------------------------------------------------------
export { FoldableSectionsSolidGfxApp };
